import React, { useState, useEffect } from 'react';
import './NewProduct.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faTimes, faCheck, faBox, faTag, faFolder, faTrash,
    faTshirt, faPalette, faRuler, faRulerHorizontal, faInfoCircle, faDollarSign,
    faWarehouse, faEdit, faCog, faScissors, faSync
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';
import { useColors } from '../../../hooks/useColors';
import { useTallas } from '../../../hooks/useTallas';
import ColorManager from '../ColorManager/ColorManager';
import TallaManager from '../TallaManager/TallaManager';
import NewCategoria from '../NewCategoria/NewCategoria';

// ============= Toast =============
const Toast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="product-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`product-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function NewProduct() {
    // ======= Hook de colores =======
    const { coloresActivos, cargarColores } = useColors();
    const { tallasActivas, tallasComoStrings, tiposTallas, cargarTallas, cargarTiposTallas, obtenerTallasPorTipo } = useTallas();

    // ======= básicos =======
    const [modalOpen, setModalOpen] = useState(false);
    const [mensaje, setMensaje] = useState('');
    const [toasts, setToasts] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [showColorManager, setShowColorManager] = useState(false);
    const [showTallaManager, setShowTallaManager] = useState(false);

    // producto
    const [nombreProducto, setNombreProducto] = useState('');
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');

    // precios
    const [precioProducto, setPrecioProducto] = useState('');
    const [precioAnteriorProducto, setPrecioAnteriorProducto] = useState('');
    const [costoProducto, setCostoProducto] = useState('');

    // variaciones acumuladas (cada item = una combinación color+talla con stock propio)
    const [variaciones, setVariaciones] = useState([]);

    // NUEVO ESTADO PARA SABER QUÉ VARIACIÓN ESTAMOS EDITANDO
    const [idVariacionAEditar, setIdVariacionAEditar] = useState(null);

    // form de variación (en edición)
    const [colorActual, setColorActual] = useState('');
    const [tallasSeleccionadas, setTallasSeleccionadas] = useState([]); // string[]
    const [stockPorTalla, setStockPorTalla] = useState({});             // { [talla]: number | '' }
    const [imagenesDraft, setImagenesDraft] = useState([]);
    const [descripcion, setDescripcion] = useState('');

    // tallas - usando el sistema de gestión de tallas
    const [tallasDisponibles, setTallasDisponibles] = useState([]);
    const [tipoTallaSeleccionado, setTipoTallaSeleccionado] = useState('');

    // ... otros useState
    const [mostrarFormularioVariacion, setMostrarFormularioVariacion] = useState(true);

    // Usar colores dinámicos desde la base de datos
    const coloresDisponibles = coloresActivos;

    // ===== effects =====
    useEffect(() => { cargarCategorias(); }, []);

    // Cargar tallas basadas en el tipo seleccionado
    useEffect(() => {
        const cargarTallasPorTipo = async () => {
            if (tipoTallaSeleccionado) {
                const tallasDelTipo = await obtenerTallasPorTipo(parseInt(tipoTallaSeleccionado));
                setTallasDisponibles(tallasDelTipo.map(t => t.nombre));
            } else {
                // NO mostrar todas las tallas por defecto - dejar vacío
                setTallasDisponibles([]);
            }
        };

        cargarTallasPorTipo();
    }, [tipoTallaSeleccionado]); // Solo depender del tipo seleccionado

    // Limpiar tallas seleccionadas cuando cambie el tipo
    useEffect(() => {
        setTallasSeleccionadas([]);
        setStockPorTalla({});
    }, [tipoTallaSeleccionado]);

    // ===== helpers =====
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    const cargarCategorias = async () => {
        try {
            const r = await fetch(`${baseURL}/categoriasGet.php`);
            const data = await r.json();
            setCategorias(data.categorias || []);
        } catch (e) { console.error(e); showToast('Error al cargar las categorías', 'error'); }
    };

    // Al crear una categoría desde el modal embebido: refrescar lista y seleccionarla
    const handleCategoriaCreada = async (cat) => {
        await cargarCategorias();
        if (cat?.idCategoria) {
            setCategoriaSeleccionada(String(cat.idCategoria));
        }
        showToast(`Categoría "${cat?.categoria || ''}" creada y seleccionada`, 'success');
    };

    // Funciones para manejar el administrador de tallas
    const abrirTallaManager = () => {
        setShowTallaManager(true);
    };

    // modal
    const toggleModal = () => { if (modalOpen) resetForm(); setModalOpen(!modalOpen); };

    const resetForm = () => {
        setNombreProducto('');
        setCategoriaSeleccionada('');
        setPrecioProducto('');
        setPrecioAnteriorProducto('');
        setCostoProducto('');
        setVariaciones([]);
        setTipoTallaSeleccionado('');
        resetFormVariacion();
        setMensaje('');
    };

    const resetFormVariacion = () => {
        setIdVariacionAEditar(null); // MUY IMPORTANTE: Resetear el ID
        setColorActual('');
        setTallasSeleccionadas([]);
        setStockPorTalla({});
        setImagenesDraft([]);
    };

    // RENOMBRAMOS LA FUNCIÓN AGREGAR POR GUARDAR
    // Tu función de guardarVariacion actualizada
    const guardarVariacion = () => {
        // ... (Tu lógica de validación actual)
        if (!colorActual) return showToast('Seleccione un color', 'warning');
        if (!tallasSeleccionadas.length) return showToast('Seleccione al menos una talla', 'warning');

        // Aquí es donde definimos 'tallas'
        const tallas = [];
        for (const t of tallasSeleccionadas) {
            const s = Number(stockPorTalla?.[t] ?? 0);
            if (s < 0 || Number.isNaN(s)) return showToast(`Stock inválido para talla ${t}`, 'warning');
            tallas.push({ talla: t, stock: s });
        }

        const colorObj = coloresDisponibles.find(c => c.nombre === colorActual);

        // CAMBIO CLAVE: filtramos para asegurar que solo haya objetos de imagen válidos
        const imagenesValidas = imagenesDraft.filter(img => img.file);
        if (imagenesValidas.length > 0 && !imagenesValidas.some(i => i.esPrincipal)) {
            imagenesValidas[0].esPrincipal = true;
        }

        const variacionActualizada = {
            id: idVariacionAEditar || Date.now(),
            color: colorActual,
            colorHex: colorObj ? colorObj.valor : '#000000',
            tallas: tallas,
            // Almacenamos el objeto de imagen completo, incluyendo el 'preview' que es la URL temporal
            imagenes: imagenesValidas.map(img => ({ ...img }))
        };

        if (idVariacionAEditar) {
            setVariaciones(prev => prev.map(v => v.id === idVariacionAEditar ? variacionActualizada : v));
            showToast('Variación actualizada', 'success');
        } else {
            setVariaciones(prev => [...prev, variacionActualizada]);
            showToast('Variación agregada', 'success');
        }

        resetFormVariacion();
        setMostrarFormularioVariacion(false);
    };

    const crearProducto = async () => {
        let variacionesParaValidar = [...variaciones];

        // Si el formulario de agregar variación está visible, se debe validar
        // la variación actual y agregarla a la lista temporalmente.
        if (mostrarFormularioVariacion) {
            if (!colorActual || !tallasSeleccionadas.length) {
                showToast('Faltan datos en la variación actual. Por favor, complételos o agregue al menos una variación.', 'error');
                return;
            }
            const tallas = [];
            for (const t of tallasSeleccionadas) {
                const s = Number(stockPorTalla?.[t] ?? 0);
                if (s < 0 || Number.isNaN(s)) {
                    showToast(`Stock inválido para la talla ${t}`, 'error');
                    return;
                }
                tallas.push({ talla: t, stock: s });
            }
            if (variaciones.some(v => v.color === colorActual)) {
                showToast('El color de la variación ya está en la lista. Por favor, agregue un color diferente o edite la variación existente.', 'error');
                return;
            }

            const colorObj = coloresDisponibles.find(c => c.nombre === colorActual);
            const imagenesValidas = imagenesDraft.filter(img => img.file instanceof File);
            if (imagenesValidas.length > 0 && !imagenesValidas.some(i => i.esPrincipal)) {
                imagenesValidas[0].esPrincipal = true;
            }

            const nuevaVariacion = {
                id: Date.now(),
                color: colorActual,
                colorHex: colorObj ? colorObj.valor : '#000000',
                tallas,
                imagenes: imagenesValidas.map(img => ({
                    file: img.file,
                    alt: img.alt || '',
                    titulo: img.titulo || '',
                    esPrincipal: !!img.esPrincipal
                }))
            };
            variacionesParaValidar = [...variaciones, nuevaVariacion];

        } else {
            variacionesParaValidar = variaciones;
        }

        // Validación principal
        if (!validarFormulario(variacionesParaValidar)) {
            return;
        }

        // ... (El resto de la lógica para enviar FormData, que no necesita cambios)
        setMensaje('Procesando...');
        try {
            const formData = new FormData();
            formData.append('nombreProducto', nombreProducto.trim());
            formData.append('idCategoria', parseInt(categoriaSeleccionada));
            // Obtener el código del tipo de talla seleccionado
            const tipoTallaObj = tiposTallas.find(t => t.idTipoTalla === parseInt(tipoTallaSeleccionado));
            const codigoTipoTalla = tipoTallaObj ? tipoTallaObj.codigo : '';
            formData.append('tipoTalla', codigoTipoTalla);
            formData.append('precio', parseFloat(precioProducto));
            formData.append('precioAnterior', precioAnteriorProducto ? parseFloat(precioAnteriorProducto) : '');
            formData.append('costo', parseFloat(costoProducto));
            formData.append('descripcion', descripcion || '');
            const variacionesData = variacionesParaValidar.map(v => ({
                color: v.color,
                colorHex: v.colorHex,
                tallas: v.tallas,
                totalImagenes: v.imagenes ? v.imagenes.length : 0,
                imagenPrincipalIndex: v.imagenes
                    ? v.imagenes.findIndex(img => img.esPrincipal)
                    : -1
            }));
            formData.append('variaciones', JSON.stringify(variacionesData));
            let totalImagenes = 0;
            variacionesParaValidar.forEach((v, varIndex) => {
                (v.imagenes || []).forEach((img, imgIndex) => {
                    formData.append(`imagen_var_${varIndex}_${imgIndex}`, img.file);
                    formData.append(`imagen_var_${varIndex}_${imgIndex}_alt`, img.alt || '');
                    formData.append(`imagen_var_${varIndex}_${imgIndex}_titulo`, img.titulo || '');
                    formData.append(`imagen_var_${varIndex}_${imgIndex}_principal`, img.esPrincipal ? '1' : '0');
                    formData.append(`imagen_var_${varIndex}_${imgIndex}_orden`, imgIndex + 1);
                    totalImagenes++;
                });
            });
            for (let pair of formData.entries()) {
                console.log(pair[0] + ': ' + pair[1]);
            }
            formData.append('totalImagenes', totalImagenes);
            const response = await fetch(`${baseURL}/productosPost.php`, { method: 'POST', body: formData });
            const result = await response.json();
            if (result.mensaje) {
                setMensaje('');
                showToast(result.mensaje, 'success');
                toggleModal();
                setTimeout(() => { window.location.reload(); }, 1500);
            } else if (result.error) {
                setMensaje('');
                showToast(result.error, 'error');
            }
        } catch (e) {
            console.error(e);
            setMensaje('');
            showToast('Error de conexión. Inténtelo nuevamente.', 'error');
        }
    };


    const eliminarVariacion = (id) => {
        setVariaciones(prev => prev.filter(v => v.id !== id));
        // Si estamos editando esta variación, reseteamos el formulario
        if (idVariacionAEditar === id) resetFormVariacion();
        if (variaciones.length === 1) {
            setMostrarFormularioVariacion(true);
            resetFormVariacion();
        }
        showToast('Variación eliminada', 'info');
    };

    // FUNCION DE EDITAR SIN BORRAR
    const editarVariacion = (variacion) => {
        setIdVariacionAEditar(variacion.id); // Almacena el ID para saber que estamos editando

        setColorActual(variacion.color);
        const tallasParaEditar = (variacion.tallas || []).map(tallaObj => tallaObj.talla);
        setTallasSeleccionadas(tallasParaEditar);
        const stockParaEditar = {};
        (variacion.tallas || []).forEach(tallaObj => {
            stockParaEditar[tallaObj.talla] = String(tallaObj.stock ?? '');
        });
        setStockPorTalla(stockParaEditar);

        // NO NECESITAS MODIFICAR ESTO, LA LÓGICA ANTERIOR ES CORRECTA
        // YA QUE LAS IMÁGENES GUARDADAS TIENEN LA PROPIEDAD 'preview'
        setImagenesDraft(
            (variacion.imagenes || []).map(img => ({ ...img }))
        );

        setMostrarFormularioVariacion(true);
        showToast(`Editando variación de color ${variacion.color}`, 'info');
    };

    const validarFormulario = (variacionesParaValidar) => {
        if (!nombreProducto.trim()) return showToast('El nombre del producto es obligatorio', 'error'), false;
        if (!categoriaSeleccionada) return showToast('Seleccione una categoría', 'error'), false;
        if (!tipoTallaSeleccionado) return showToast('Seleccione un tipo de tallas', 'error'), false;
        if (!costoProducto || parseFloat(costoProducto) < 0) return showToast('Ingrese un costo válido', 'error'), false;
        if (!precioProducto || parseFloat(precioProducto) <= 0) return showToast('Ingrese un precio válido', 'error'), false;
        if (parseFloat(precioProducto) <= parseFloat(costoProducto)) return showToast('El precio debe ser mayor al costo', 'error'), false;
        if (variacionesParaValidar.length === 0) {
            showToast('Debe agregar al menos una variación', 'error');
            return false;
        }

        for (let i = 0; i < variacionesParaValidar.length; i++) {
            const v = variacionesParaValidar[i];
            if (!v.color) return showToast(`La variación ${i + 1} no tiene color`, 'error'), false;
            if (!Array.isArray(v.tallas) || v.tallas.length === 0) {
                return showToast(`La variación ${i + 1} no tiene tallas`, 'error'), false;
            }
            for (const t of v.tallas) {
                if (!t.talla) return showToast(`Falta el nombre de alguna talla en la variación ${i + 1}`, 'error'), false;
                const s = Number(t.stock);
                if (Number.isNaN(s) || s < 0) {
                    return showToast(`Stock inválido para la talla ${t.talla} en la variación ${i + 1}`, 'error'), false;
                }
            }
        }
        return true;
    };


    // resumen
    const resumen = (() => {
        if (variaciones.length === 0) return null;
        const stockTotal = variaciones.reduce((acc, v) => {
            const subtotal = (v.tallas || []).reduce((s, t) => s + Number(t.stock || 0), 0);
            return acc + subtotal;
        }, 0);
        const margen = precioProducto && costoProducto
            ? ((parseFloat(precioProducto) - parseFloat(costoProducto)) / parseFloat(precioProducto) * 100).toFixed(1)
            : 0;
        return { stockTotal, totalVariaciones: variaciones.length, margen };
    })();


    // picker de imágenes para la variación en edición
    // picker de imágenes para la variación en edición
    function ImagePickerVariacion({ imagenes, setImagenes }) {
        const onFiles = (files) => {
            const arr = Array.from(files || []);
            const next = arr.map(file => ({
                file,
                preview: URL.createObjectURL(file), // Para nuevas imágenes, 'preview' es la URL local
                alt: '',
                titulo: '',
                esPrincipal: false
            }));
            setImagenes(prev => {
                const merged = [...prev, ...next];
                if (!merged.some(i => i.esPrincipal) && merged.length > 0) merged[0].esPrincipal = true;
                return merged;
            });
        };
        const setPrincipal = (index) => setImagenes(prev => prev.map((img, i) => ({ ...img, esPrincipal: i === index })));
        const removeAt = (index) => setImagenes(prev => {
            const copy = [...prev];
            const was = copy[index]?.esPrincipal;

            // Si la imagen a eliminar tiene un preview (URL local creada con URL.createObjectURL)
            // y tiene un 'file' asociado, revocamos la URL para liberar memoria.
            if (copy[index]?.preview && copy[index].file) {
                URL.revokeObjectURL(copy[index].preview);
            }

            copy.splice(index, 1);
            if (was && copy.length > 0) copy[0].esPrincipal = true;
            return copy;
        });

        return (
            <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className='product-form-label'>
                    <FontAwesomeIcon icon={faScissors} size="sm" /> Imágenes de la variación (opcional)
                </label>
                <input type="file" accept="image/*" multiple className="product-form-input" onChange={(e) => onFiles(e.target.files)} />
                {imagenes.length > 0 && (
                    <div className="product-image-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 12 }}>
                        {imagenes.map((img, idx) => (
                            <div key={idx} className="image-thumb" style={{ border: '1px solid var(--color2)', borderRadius: 8, padding: 8 }}>
                                <div style={{ position: 'relative', marginBottom: 8 }}>
                                    {/* CAMBIO CLAVE AQUÍ: Usamos img.preview o img.url como fallback para el src */}
                                    {/* Asegúrate de que img.preview contenga la URL local (para archivos nuevos) o la URL del servidor (para archivos existentes) */}
                                    <img
                                        src={img.preview || img.url} // Usamos 'preview' si está disponible, si no, 'url'
                                        alt={img.alt || `img-${idx}`}
                                        style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6 }}
                                    />
                                    {img.esPrincipal && (
                                        <span style={{ position: 'absolute', top: 6, left: 6, background: 'var(--color2)', color: 'var(--blanco)', fontSize: 10, padding: '2px 6px', borderRadius: 6 }}>Principal</span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button type="button" className="btn-product-primary" onClick={() => setPrincipal(idx)}>Marcar principal</button>
                                    <button type="button" className="global-btn-delete" onClick={() => removeAt(idx)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ===== UI =====
    return (
        <div className='NewContain'>
            <Toast toasts={toasts} />

            <button onClick={toggleModal} className='btn-product-primary flex-center' style={{ gap: 8 }}>
                <FontAwesomeIcon icon={faPlus} size="sm" /> Nuevo Producto
            </button>

            {modalOpen && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 900 }}>
                        {/* Header */}
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faBox} size="lg" /> Crear Nuevo Producto
                            </div>
                            <button className='product-close-button' onClick={toggleModal}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className='product-modal-body'>
                            {/* Información Básica */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faBox} /> Información Básica del Producto</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faTag} size="sm" />Titulo *</label>
                                            <input className='product-form-input' value={nombreProducto} onChange={e => setNombreProducto(e.target.value)} maxLength={100} />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faFolder} size="sm" /> Categoría *</label>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <select className='product-form-input' value={categoriaSeleccionada} onChange={e => setCategoriaSeleccionada(e.target.value)} style={{ flex: 1, minWidth: 0 }}>
                                                    <option value="">Seleccionar categoría</option>
                                                    {categorias.map(c => <option key={c.idCategoria} value={c.idCategoria}>{c.categoria}</option>)}
                                                </select>
                                                <NewCategoria
                                                    embedded
                                                    buttonLabel=""
                                                    buttonClassName="btn-product-icon"
                                                    buttonTitle="Crear categoría nueva"
                                                    buttonStyle={{
                                                        padding: '6px', width: '32px', height: '32px', fontSize: '12px',
                                                        borderRadius: '6px', border: '1px solid #d1d5db', background: '#f9fafb',
                                                        color: '#6b7280', cursor: 'pointer', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                    }}
                                                    onCreated={handleCategoriaCreada}
                                                />
                                            </div>
                                            <small style={{ color: '#6b7280' }}>¿No existe? Créala con el botón +</small>
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faRulerHorizontal} size="sm" /> Tipo de Tallas *</label>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <select
                                                    className='product-form-input'
                                                    value={tipoTallaSeleccionado}
                                                    onChange={(e) => setTipoTallaSeleccionado(e.target.value)}
                                                    style={{ flex: 1, minWidth: 0 }}
                                                >
                                                    <option value="">Selecciona un grupo de tallas...</option>
                                                    {tiposTallas.map(tipo => (
                                                        <option key={tipo.idTipoTalla} value={tipo.idTipoTalla}>
                                                            {tipo.nombre} ({tipo.cantidadTallas || 0} tallas)
                                                        </option>
                                                    ))}
                                                </select>

                                                <button
                                                    type="button"
                                                    onClick={abrirTallaManager}
                                                    className="btn-product-icon"
                                                    title={`Administrar tallas (${tallasDisponibles.length} disponibles en este grupo)`}
                                                    style={{
                                                        padding: '6px',
                                                        width: '32px',
                                                        height: '32px',
                                                        fontSize: '12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #d1d5db',
                                                        background: '#f9fafb',
                                                        color: '#6b7280',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCog} size="sm" />
                                                </button>

                                            </div>
                                            {!tipoTallaSeleccionado && (
                                                <small style={{ color: '#f59e0b' }}>
                                                    Selecciona primero un grupo para ver las tallas disponibles
                                                </small>
                                            )}
                                        </div>


                                        <div className='product-form-group'>
                                            <label className='product-form-label'>
                                                <FontAwesomeIcon icon={faTshirt} size="sm" /> Descripción (opcional)
                                            </label>
                                            <textarea
                                                className='product-form-input'
                                                value={descripcion}
                                                onChange={e => setDescripcion(e.target.value)}
                                                maxLength={300}
                                                rows={4}
                                            />
                                        </div>

                                    </div>

                                </div>
                            </div>





                            {/* Lista de variaciones */}
                            {variaciones.length > 0 && (
                                <div className="product-section-card animate-stagger-2 mb-24">
                                    <div className="product-section-header">
                                        <div className="product-section-title"><FontAwesomeIcon icon={faRuler} /> Variaciones del Producto</div>
                                        <div className="product-section-badge">{variaciones.length} variaciones</div>
                                    </div>
                                    <div className="product-section-content" >
                                        <div className="global-table-container">
                                            <table className="global-table">
                                                <thead>
                                                    <tr>
                                                        <th>Color</th>
                                                        <th>Tallas (stock)</th>
                                                        <th>Acciones</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {variaciones.map((v, index) => (
                                                        <tr key={v.id} style={{ animationDelay: `${index * 0.1}s` }}>
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <div style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: v.colorHex, border: '1px solid #ccc' }} />
                                                                    {v.color}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                                    {(v.tallas || []).map(t => (
                                                                        <span key={t.talla} className="global-indicator success">
                                                                            {t.talla} · {t.stock}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="global-table-actions">
                                                                    <button className='global-btn-edit' onClick={() => editarVariacion(v)} title="Editar variación">
                                                                        <FontAwesomeIcon icon={faEdit} />
                                                                    </button>
                                                                    <button className='global-btn-delete' onClick={() => eliminarVariacion(v.id)} title="Eliminar variación">
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </table>
                                        </div>
                                        {variaciones.length > 0 && !mostrarFormularioVariacion && (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                                <button
                                                    type='button'
                                                    onClick={() => {
                                                        setMostrarFormularioVariacion(true);
                                                        // Opcional: limpiar los campos del formulario de variación si el usuario canceló
                                                        // resetFormVariacion();
                                                    }}
                                                    className='btn-product-primary'
                                                    style={{ padding: '8px 16px', fontSize: 14 }}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: 8 }} /> Agregar otra variación
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Agregar variación (multi-talla con stock por talla) */}
                            {tallasDisponibles.length > 0 && mostrarFormularioVariacion && (
                                <div className="product-section-card mb-24">
                                    <div className="product-section-header">
                                        <div className="product-section-title"><FontAwesomeIcon icon={faPalette} /> Agregar Variación (Color + Tallas)</div>
                                        <div className="product-section-badge">Tallas Gestionadas ({tallasDisponibles.length} disponibles)</div>
                                    </div>

                                    <div className="product-section-content">
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                                            <div className='product-form-group'>
                                                <label className='product-form-label'>
                                                    <FontAwesomeIcon icon={faPalette} size="sm" /> Color *
                                                </label>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <select
                                                        className='product-form-input'
                                                        value={colorActual}
                                                        onChange={e => setColorActual(e.target.value)}
                                                        style={{ flex: 1, minWidth: 0 }}
                                                    >
                                                        <option value="">Seleccionar color</option>
                                                        {coloresDisponibles.map(c => <option key={c.idColor || c.nombre} value={c.nombre}>{c.nombre}</option>)}
                                                    </select>
                                                    {colorActual && (
                                                        <div style={{ marginTop: 8, width: 30, height: 20, borderRadius: 4, backgroundColor: coloresDisponibles.find(c => c.nombre === colorActual)?.valor, border: '1px solid #ccc' }} />
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowColorManager(true)}
                                                        className="btn-product-icon"
                                                        title={`Administrar colores (${coloresDisponibles.length} disponibles)`}
                                                        style={{
                                                            padding: '6px',
                                                            width: '32px',
                                                            height: '32px',
                                                            fontSize: '12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #d1d5db',
                                                            background: '#f9fafb',
                                                            color: '#6b7280',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCog} size="sm" />
                                                    </button>
                                                </div>
                                                {coloresDisponibles.length === 0 && (
                                                    <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, fontSize: 12 }}>
                                                        No hay colores disponibles. Haz clic en el ícono de configuración para agregar colores.
                                                    </div>
                                                )}
                                            </div>

                                            {/* Selector de tallas */}
                                            <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                                                <label className="product-form-label"><FontAwesomeIcon icon={faTshirt} size="sm" /> Tallas *</label>
                                                {!tipoTallaSeleccionado ? (
                                                    <div className="empty-description" style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                                                        <FontAwesomeIcon icon={faRulerHorizontal} style={{ marginRight: '8px', color: '#f59e0b' }} />
                                                        Primero selecciona un <strong>Tipo de Tallas</strong> arriba para ver las tallas disponibles de ese grupo.
                                                    </div>
                                                ) : tallasDisponibles.length === 0 ? (
                                                    <div className="empty-description" style={{ background: '#fef2f2', padding: '12px', borderRadius: '6px', border: '1px solid #ef4444' }}>
                                                        <FontAwesomeIcon icon={faTshirt} style={{ marginRight: '8px', color: '#ef4444' }} />
                                                        No hay tallas configuradas en este grupo. Ve al <strong>Administrar Tallas</strong> para agregar tallas.
                                                    </div>
                                                ) : (
                                                    <div className="tallas-grid">
                                                        {tallasDisponibles.map((t) => {
                                                            const checked = tallasSeleccionadas.includes(t);
                                                            return (
                                                                <button
                                                                    key={t}
                                                                    type="button"
                                                                    className={`talla-chip ${checked ? 'selected' : ''}`}
                                                                    onClick={() => {
                                                                        setTallasSeleccionadas(prev => {
                                                                            const next = checked ? prev.filter(x => x !== t) : [...prev, t];
                                                                            // inicializa o limpia stock por talla
                                                                            setStockPorTalla(sp => {
                                                                                const copy = { ...sp };
                                                                                if (!checked && copy[t] === undefined) copy[t] = '';
                                                                                if (checked) delete copy[t];
                                                                                return copy;
                                                                            });
                                                                            return next;
                                                                        });
                                                                    }}
                                                                >
                                                                    {t}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {tipoTallaSeleccionado && (
                                                    <div className="tallas-counter">
                                                        {tallasSeleccionadas.length} de {tallasDisponibles.length} talla(s) seleccionada(s)
                                                        {tiposTallas.find(t => t.idTipoTalla == parseInt(tipoTallaSeleccionado)) && (
                                                            <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                                                                del grupo "{tiposTallas.find(t => t.idTipoTalla == parseInt(tipoTallaSeleccionado))?.nombre}"
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Stock por talla seleccionada */}
                                            {tallasSeleccionadas.length > 0 && (
                                                <div className="product-form-group" style={{ gridColumn: '1 / -1' }}>
                                                    <div className="product-form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        <FontAwesomeIcon icon={faWarehouse} size="sm" />
                                                        Stock por talla *
                                                    </div>

                                                    <div className="tallas-stock-grid">
                                                        {tallasSeleccionadas.map((t) => (
                                                            <div key={t} className="talla-stock-card">
                                                                <span className="talla-pill">{t}</span>
                                                                <div className="talla-input-row">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        inputMode="numeric"
                                                                        className="product-form-input"
                                                                        value={stockPorTalla[t] ?? ''}
                                                                        onChange={(e) =>
                                                                            setStockPorTalla((prev) => ({ ...prev, [t]: e.target.value }))
                                                                        }
                                                                        placeholder="0"
                                                                    />
                                                                    <span className="talla-sufijo">unid.</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                        </div>

                                        <ImagePickerVariacion imagenes={imagenesDraft} setImagenes={setImagenesDraft} />

                                        {/* Nuevo contenedor para los botones */}
                                        <div className="action-buttons-container">
                                            <button
                                                type='button'
                                                onClick={guardarVariacion}
                                                className='btn-product-primary flex-center'
                                            >
                                                <FontAwesomeIcon icon={faPlus} size="sm" /> {idVariacionAEditar ? 'Guardar Cambios' : 'Agregar Variación(es)'}
                                            </button>
                                            {variaciones.length > 0 && (
                                                <button
                                                    type='button'
                                                    onClick={() => {
                                                        setMostrarFormularioVariacion(false);
                                                        resetFormVariacion();
                                                    }}
                                                    className='global-btn-delete flex-center'
                                                >
                                                    <FontAwesomeIcon icon={faTimes} /> Cancelar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}


                            {/* Estado vacío */}


                            {/* Precios */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faDollarSign} /> Precios del Producto</div>
                                    <div className="product-section-badge">Aplica a todas las variaciones</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'>💰 Costo del Producto *</label>
                                            <input type='number' className='product-form-input' value={costoProducto} onChange={e => setCostoProducto(e.target.value)} min="0" step="0.01" />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faDollarSign} size="sm" /> Precio de Venta *</label>
                                            <input type='number' className='product-form-input' value={precioProducto} onChange={e => setPrecioProducto(e.target.value)} min="0" step="0.01" />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faDollarSign} size="sm" /> Precio Anterior (Opcional)</label>
                                            <input type='number' className='product-form-input' value={precioAnteriorProducto} onChange={e => setPrecioAnteriorProducto(e.target.value)} min="0" step="0.01" />
                                        </div>
                                    </div>

                                    {precioProducto && costoProducto && parseFloat(precioProducto) > 0 && parseFloat(costoProducto) > 0 && (
                                        <div style={{ marginTop: 16, padding: 12, background: parseFloat(precioProducto) > parseFloat(costoProducto) ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', borderRadius: 8, border: `1px solid ${parseFloat(precioProducto) > parseFloat(costoProducto) ? '#10b981' : '#ef4444'}` }}>
                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Análisis de Rentabilidad:</div>
                                            <div style={{ color: parseFloat(precioProducto) > parseFloat(costoProducto) ? '#16a34a' : '#dc2626', fontSize: 14 }}>
                                                {parseFloat(precioProducto) > parseFloat(costoProducto)
                                                    ? (<><strong>Margen: {(((parseFloat(precioProducto) - parseFloat(costoProducto)) / parseFloat(precioProducto)) * 100).toFixed(1)}%</strong><br />Ganancia por unidad: ${(parseFloat(precioProducto) - parseFloat(costoProducto)).toFixed(2)}</>)
                                                    : (<strong>⚠️ El precio debe ser mayor al costo</strong>)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className='product-submit-section'>
                            <button type='button' onClick={toggleModal} className='btn-product-secondary'>Cancelar</button>
                            {mensaje ? (
                                <button type='button' className='btn-product-primary' disabled style={{ opacity: .6 }}>
                                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} /> {mensaje}
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={crearProducto} // <-- Llama directamente a la función crearProducto
                                    className='btn-product-primary flex-center'
                                    style={{ gap: 8 }}
                                    disabled={!nombreProducto.trim() || !categoriaSeleccionada || !costoProducto || !precioProducto}
                                >
                                    <FontAwesomeIcon icon={faCheck} size="sm" /> Crear Producto
                                    {variaciones.length > 0 && (
                                        <span style={{ background: 'rgba(255,255,255,.2)', padding: '2px 6px', borderRadius: 10, fontSize: '.75rem' }}>
                                            {variaciones.length} variaciones
                                        </span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal del administrador de colores */}
            {showColorManager && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 1000 }}>
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faPalette} size="lg" /> Administrador de Colores
                            </div>
                            <button className='product-close-button' onClick={() => setShowColorManager(false)}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        <div className='product-modal-body'>
                            <ColorManager
                                onColorsUpdated={(nuevosColores) => {
                                    cargarColores();
                                }}
                            />
                        </div>

                        <div className='product-submit-section'>
                            <button
                                type='button'
                                onClick={() => setShowColorManager(false)}
                                className='btn-product-primary'
                            >
                                <FontAwesomeIcon icon={faCheck} size="sm" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal del administrador de tallas */}
            {showTallaManager && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 1000 }}>
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faRulerHorizontal} size="lg" /> Administrador de Tallas
                            </div>
                            <button className='product-close-button' onClick={() => setShowTallaManager(false)}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        <div className='product-modal-body'>
                            <TallaManager
                                onTallasUpdated={(nuevasTallas) => {
                                    cargarTallas();
                                }}
                            />
                        </div>

                        <div className='product-submit-section'>
                            <button
                                type='button'
                                onClick={() => setShowTallaManager(false)}
                                className='btn-product-primary'
                            >
                                <FontAwesomeIcon icon={faCheck} size="sm" /> Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}